// src/pages/TrackCreation/Step1.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import shell from "../../pages/track/create/createShell.module.css";
import { useAddonAccess } from "../../hooks/useAddonAccess";

interface Subcategory {
    name: string;
    step: string;
}

interface SidebarItem {
    title: string;
    step?: string;
    hasSubcategories: boolean;
    subcategories?: Subcategory[];
}

const WaypointSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Check if the VIDEO addon is available to conditionally label the audio step
    const { hasAccess: hasVideoAddon } = useAddonAccess("VIDEO");

    // Replaces the current "waypoint-*" segment with the new step.
    const replaceStepInUrl = (newStep: string): string => {
        let updatedPath: string;
        if (newStep === "waypoint-language") {
            // Special case: remove everything after '-temp-' in the waypointId.
            if (location.pathname.includes("-temp")) {
                updatedPath = location.pathname.replace(/\/waypoint-[^/]+\/([^/]+)(-temp-.+)?$/, (_, waypointId) => {
                    const cleanWaypointId = waypointId.includes("-temp-") ? waypointId.split("-temp-")[0] : waypointId;
                    return `/${newStep}/${cleanWaypointId}`;
                });
            } else {
                updatedPath = location.pathname.replace(/waypoint-[^/]+/, newStep);
            }
        } else if (newStep === "map-content") {
            // General case: Replace "waypoint-[step]" and everything after it.
            updatedPath = location.pathname.replace(/\/waypoint-[^/]+\/.+$/, "/map-content");
        } else {
            updatedPath = location.pathname.replace(/waypoint-[^/]+/, newStep);
        }
        return updatedPath;
    };

    // Extracts a value from the URL using a regex pattern.
    const extractValueFromPattern = (): string | null => {
        const regex = /^\/create\/tour\/[^/]+\/waypoint-[^/]+\/[^/]+\/([^/]+)$/;
        const match = location.pathname.match(regex);
        return match ? match[1] : null;
    };

    // Define sidebar items with subcategories.
    const sidebarItems: SidebarItem[] = [
        {
            title: t("waypointDetails"),
            hasSubcategories: true,
            subcategories: [
                { name: t("language"), step: "waypoint-language" },
                ...(extractValueFromPattern() !== null
                    ? [
                          { name: t("title"), step: "waypoint-title" },
                          { name: t("coordinates"), step: "waypoint-coordinates" },
                          { name: t("text"), step: "waypoint-text" },
                          { name: hasVideoAddon ? t("mediaContent") : t("audio"), step: "waypoint-audio" },
                          { name: t("photos"), step: "waypoint-photos" },
                          { name: t("waypointAdditionalInfo"), step: "waypoint-info" },
                      ]
                    : []),
            ],
        },
    ];

    return (
        <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {!item.hasSubcategories ? (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    item.step && location.pathname.includes(item.step) ? shell.navTitleLinkActive : ""
                                }`}
                            >
                                <Link to={replaceStepInUrl(item.step || "")} className="sidebar-link">
                                    {item.title}
                                </Link>
                            </li>
                        ) : (
                            <>
                                <li className={`sidebar-title ${shell.navHeading}`}>{item.title}</li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories?.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname.includes(sub.step) ? shell.navSubItemActive : ""
                                            }`}
                                        >
                                            <Link to={replaceStepInUrl(sub.step)} className="sidebar-link">
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                ))}
            </ul>
            <Button
                label={t("backToTheMap")}
                variant="outline"
                shape="pill"
                className={shell.navBackBtn}
                onClick={() => {
                    navigate(location.pathname.replace(/\/waypoint-[^/]+\/.+$/, "/map-content"));
                }}
            />
        </div>
    );
};

export default WaypointSidebar;
